import type { ComponentType, CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, useReducedMotion } from 'motion/react';
import { MessagesSquare, Users } from 'lucide-react';
import {
  Blob,
  Button,
  Container,
  Parallax,
  Reveal,
  SectionHeader,
  Sticker,
  Wave,
  flavorVar,
} from '@/components/ui';
import type { Flavor } from '@/components/ui';
import { useSession } from '@/lib/session';
import { useSeo } from '@/lib/seo';

/** A compact "this app also does X" card — flavored blob icon + one tight line.
 * No media block: the section header already sets up both features. */
function FeatureCard({
  eyebrow,
  title,
  body,
  flavor,
  icon: Icon,
}: {
  eyebrow: string;
  title: string;
  body: string;
  flavor: Flavor;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial="rest"
      animate="rest"
      whileHover={reduce ? undefined : 'hover'}
      variants={{ rest: { y: 0 }, hover: { y: -3 } }}
      transition={{ type: 'spring', stiffness: 400, damping: 22 }}
      style={{ '--tile-hue': flavorVar(flavor) } as CSSProperties}
      className="flex h-full items-center gap-4 rounded-tile border-2 border-hairline bg-surface p-5 transition-[border-color,box-shadow] duration-300 hover:border-(--tile-hue) hover:shadow-hard"
    >
      <motion.span
        aria-hidden
        className="rounded-blob grid size-12 shrink-0 place-items-center border-2 border-ink"
        style={{ backgroundColor: flavorVar(flavor, 'soft') }}
        variants={{ rest: { rotate: 0 }, hover: { rotate: [0, -12, 12, -6, 0] } }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
      >
        <Icon className="size-6" strokeWidth={2} />
      </motion.span>
      <div className="min-w-0">
        <span className="eyebrow">{eyebrow}</span>
        <h3 className="font-display text-lg font-semibold leading-tight">{title}</h3>
        <p className="mt-1 text-sm text-ink-soft">{body}</p>
      </div>
    </motion.div>
  );
}

const STEPS = [
  {
    n: '01',
    flavor: 'berry' as const,
    title: 'Check in',
    body: 'Land at a concert, a game, a café. Nearmirer finds the Spot for you, or punch in the 4-letter code.',
  },
  {
    n: '02',
    flavor: 'grape' as const,
    title: 'Glow on',
    body: 'Flip your Glow to be visible. Drop a one-line look ("green jacket, section C"). It switches itself off after a couple of hours.',
  },
  {
    n: '03',
    flavor: 'melon' as const,
    title: 'Send a Spark',
    body: 'See someone across the room on your Radar. Tap them. Send one anonymous line. They never find out it was you.',
  },
];

const FAQ = [
  [
    'Is it really anonymous?',
    'Sparks and Whispers hide your identity from the other person. We keep just enough behind the scenes to act on reports, and nothing a moderator can casually browse.',
  ],
  [
    'Do people see my exact spot on a map?',
    'Nope. GPS is fuzzy in a crowd, so you get a distance ring, a rough direction arrow, and a look description. Your eyes do the rest.',
  ],
  ['Who can use it?', 'Anyone 18 or older, anywhere in the world.'],
  [
    'What if someone is creepy?',
    'Block them instantly and report with one tap. Reports land with a real moderation team. Repeat offenders get muted, then shown the door.',
  ],
];

export function HomePage() {
  const { isAuthed } = useSession();
  const cta = isAuthed ? '/app' : '/signup';
  const ctaLabel = isAuthed ? 'Open Nearmirer' : 'Sign up free';
  useSeo({
    title: 'Nearmirer — Meet People Near You, Anonymously',
    description:
      "See who's near you right now — at a concert, a game, a café, or your own block — and send an anonymous compliment. The near-me app for meeting nearby people and making a new nearby friend.",
    path: '/',
  });

  return (
    <>
      <section
        className="relative overflow-hidden pb-24"
        style={{ backgroundColor: flavorVar('lychee', 'soft') }}
      >
        <Parallax speed={-80} className="pointer-events-none absolute -right-24 -top-10">
          <Blob flavor="berry" size={380} />
        </Parallax>
        <Container className="relative pt-16 sm:pt-24">
          <Reveal>
            <Sticker flavor="citron" rotate={-4}>
              near + admirer · 18+
            </Sticker>
          </Reveal>
          <h1 className="mt-6 max-w-3xl text-[clamp(2.75rem,7vw,5rem)] font-semibold leading-[1.02]">
            That person across the room?
            <span style={{ color: flavorVar('berry', 'ink') }}> Say something.</span>
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink-soft">
            Nearmirer shows you people nearby who want to be noticed, whether that’s at a show, a
            match, or a bar, and lets you send one anonymous line. Nameless, no big deal.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild variant="candy" size="lg">
              <Link to={cta}>{ctaLabel}</Link>
            </Button>
            {!isAuthed && (
              <Button asChild variant="outline" size="lg">
                <Link to="/login">Log in</Link>
              </Button>
            )}
          </div>
          <p className="mt-4 text-sm text-ink-soft">
            <Link to="/safety" className="font-semibold underline">
              How we keep it safe
            </Link>
          </p>
        </Container>
        <Wave flavor="melon" shade="soft" position="bottom" variant="drip" height={70} />
      </section>

      <section className="relative bg-flavor-melon-soft pb-24 pt-16">
        <Container>
          <SectionHeader eyebrow="How it works" title="Three taps, one Spark." />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.07}>
                <div className="h-full rounded-tile border-2 border-ink bg-surface p-6 shadow-hard">
                  <span
                    className="inline-block rounded-pill border-2 border-ink px-2 py-0.5 font-display text-xs font-semibold"
                    style={{ backgroundColor: flavorVar(s.flavor, 'soft') }}
                  >
                    {s.n}
                  </span>
                  <h3 className="mt-3 text-xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-ink-soft">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
        <Wave flavor="peach" shade="soft" position="bottom" variant="roll" height={64} />
      </section>

      <section className="relative bg-flavor-peach-soft pb-24 pt-16">
        <Container>
          <SectionHeader
            eyebrow="More than a Spark"
            title="Whispers & Circles."
            description="A Spark can turn into a private, still-anonymous 1:1 thread. And every place has a live group chatroom where nobody knows who's who."
          />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <Reveal>
              <FeatureCard
                eyebrow="Whisper"
                title="Anonymous 1:1"
                body="They said yes to your Spark? Keep talking. Both sides stay pseudonymous until you both decide otherwise."
                flavor="grape"
                icon={MessagesSquare}
              />
            </Reveal>
            <Reveal delay={0.08}>
              <FeatureCard
                eyebrow="Circle"
                title="The room, talking"
                body="Every venue has a live group chat. Ephemeral nickname, moderated, gone when you leave."
                flavor="berry"
                icon={Users}
              />
            </Reveal>
          </div>
        </Container>
        <Wave flavor="lychee" shade="soft" position="bottom" variant="bump" height={64} />
      </section>

      <section className="relative bg-flavor-lychee-soft pb-24 pt-16">
        <Container>
          <SectionHeader eyebrow="Straight answers" title="You probably want to know…" />
          <div className="mt-10 max-w-2xl divide-y-2 divide-hairline">
            {FAQ.map(([q, a]) => (
              <motion.details key={q} className="group py-4">
                <summary className="cursor-pointer list-none font-display font-semibold">
                  <span className="mr-2 text-accent group-open:hidden">+</span>
                  <span className="mr-2 hidden text-accent group-open:inline">–</span>
                  {q}
                </summary>
                <p className="mt-2 pl-6 text-sm text-ink-soft">{a}</p>
              </motion.details>
            ))}
          </div>
        </Container>
        <Wave color="var(--color-solid)" position="bottom" variant="roll" height={70} />
      </section>

      <section className="relative overflow-hidden bg-solid pb-14 pt-20 text-on-solid">
        <Container className="relative flex flex-col items-start gap-6">
          <Reveal as="h2" className="max-w-2xl text-3xl font-semibold sm:text-[2.5rem]">
            Someone at your next show is hoping you say something.
          </Reveal>
          <Reveal delay={0.08} className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button asChild size="lg" variant="candy">
              <Link to={cta}>{ctaLabel}</Link>
            </Button>
            {!isAuthed && (
              <span className="text-sm opacity-80">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold underline">
                  Log in
                </Link>
              </span>
            )}
          </Reveal>
          <Reveal delay={0.12} as="p" className="text-xs opacity-70">
            18+ only. By joining you agree to our{' '}
            <Link to="/terms" className="underline">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>
            .
          </Reveal>
        </Container>
      </section>
    </>
  );
}
